/**
 * ══════════════════════════════════════════════════════════════════════════
 * GÖNDERİ ZAMAN DAMGASI — makine-okunur zaman (saf: DOM yok, I/O yok)
 * ══════════════════════════════════════════════════════════════════════════
 *
 * ── ÖLÇÜLEN KUSUR (2026-08-25) ───────────────────────────────────────────
 * `PostMobile.tsx:279` uygulamanın **TEK** `<time>` elemanını basıyordu ve
 * makine-okunur değerini şöyle üretiyordu:
 *
 *     <time dateTime={String(post?.tarih || "")}>
 *
 * `post.tarih` gerçek bir Firestore `Timestamp`tir. Ölçüldü (node):
 *
 *     String(new Timestamp(...))  →  "[object Object]"
 *
 * Yani mobil akıştaki **her gönderi** `<time datetime="[object Object]">`
 * basıyordu. `datetime` HTML spec'inde ISO-8601 ister; geçersiz değer
 * elemanı sessizce anlamsız kılar — ekran okuyucu tarihi okuyamaz, tarayıcı
 * ve tarayıcı-dışı ayrıştırıcılar (crawler, okuma modu, paylaşım önizlemesi)
 * hiçbir zaman elde edemez. **Görsel metin doğruydu**, bu yüzden gözle
 * bakan hiç kimse göremezdi — kusur yalnız makine tarafındaydı.
 *
 * Masaüstü ikizinde (`Post.tsx:340`) durum farklı bir şekilde aynıydı:
 * orada `<time>` **HİÇ YOKTU** (`<p className="postDk-date">`), yani
 * makine-okunur zaman sıfırdı. İki yüzey, tek sınıf.
 *
 * ── NEDEN `zamanMs` ÜZERİNE KURULU ───────────────────────────────────────
 * Kendi ayrıştırıcısını yazmak, `utils/zamanOtoritesi.ts`in kapattığı
 * saniye/milisaniye ayracı kusurunun **yedinci kopyasını** doğururdu.
 * Bu modül tek otoriteyi çağırır; tanınan yedi biçim oradan MİRAS ALINIR
 * (sayı-saniye · sayı-ms · ISO metin · `Date` · `Timestamp` · `{seconds}` ·
 * `{_seconds}` · `{toDate()}`).
 *
 * ── SÖZLEŞME: ÇÖZÜLEMEYEN ZAMAN ATTRIBUTE ÜRETMEZ ────────────────────────
 * `zamanMs` çözemediğinde `0` döner ("zaman yok", 1970 değil). Bu modül o
 * hâlde **boş metin** döner ve çağrı yeri attribute'u `undefined` ile hiç
 * basmaz. Gerekçe: geçersiz bir `datetime`, olmayan bir `datetime`den
 * DAHA KÖTÜDÜR — ayrıştırıcıya yanlış veri vaat eder. Eski gövdenin
 * "[object Object]"ı tam olarak bu yanlış vaatti.
 * ══════════════════════════════════════════════════════════════════════════
 */
import { zamanMs } from "./utils/zamanOtoritesi";

/**
 * `<time datetime="…">` için ISO-8601 metni; çözülemezse `""`.
 *
 * ⚠️ ASLA `String(deger)` yapmaz — kusurun kendisi oydu.
 */
export function isoDamga(deger: unknown): string {
  const ms = zamanMs(deger);
  if (!ms) return "";
  const d = new Date(ms);
  // `zamanMs` sonlu bir sayı garantiler ama takvim aralığı dışı bir değer
  // (ör. 1e15 saniye) `Invalid Date` üretebilir → `toISOString()` fırlatır.
  const t = d.getTime();
  if (!Number.isFinite(t)) return "";
  try {
    return d.toISOString();
  } catch {
    return "";
  }
}

/**
 * Fare üstüne gelince görünen tam tarih (`title`); çözülemezse `""`.
 *
 * IG paritesi: göreli metin («2 saat önce») ekranda kalır, tam tarih
 * `title`da durur — kullanıcı kesin zamanı öğrenmek için gönderiyi
 * açmak zorunda kalmaz.
 */
export function tamTarihBasligi(deger: unknown): string {
  const ms = zamanMs(deger);
  if (!ms) return "";
  const d = new Date(ms);
  if (!Number.isFinite(d.getTime())) return "";
  try {
    return d.toLocaleString("tr-TR", {
      day: "numeric",
      month: "long",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
}

/**
 * Attribute değeri: boşsa `undefined` (React attribute'u HİÇ basmaz).
 *
 * `""` basmak `datetime=""` üretirdi — yine geçersiz bir vaat.
 */
export function damgaAttr(deger: unknown): string | undefined {
  return isoDamga(deger) || undefined;
}

/** `title` attribute'u için aynı sözleşme. */
export function baslikAttr(deger: unknown): string | undefined {
  return tamTarihBasligi(deger) || undefined;
}
